import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  FileText, Plus, Search, MoreVertical, Eye, Edit,
  Trash2, Send, DollarSign, Clock, CheckCircle, XCircle, Link2,
  Mail, MailOpen, MousePointerClick, MailPlus, Copy, Banknote, RefreshCw, Upload, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

// Sub-components
import Sidebar from "../components/dashboard/Sidebar";
import StatsCards from "../components/dashboard/StatsCards";
import MarkPaidDialog from "../components/dialogs/MarkPaidDialog";
import FollowupEmailDialog from "../components/dialogs/FollowupEmailDialog";
import MarkCompleteDialog from "../components/dialogs/MarkCompleteDialog";
import ChangeOrderDialog from "../components/dialogs/ChangeOrderDialog";
import SendBalanceDueDialog from "../components/dialogs/SendBalanceDueDialog";
import ViewChangeOrdersDialog from "../components/dialogs/ViewChangeOrdersDialog";
import CreatePaymentLinkDialog from "../components/dialogs/CreatePaymentLinkDialog";
import SendQuoteEmailDialog from "../components/dialogs/SendQuoteEmailDialog";
import UploadQuoteDialog from "../components/UploadQuoteDialog";

const STATUS_GROUPS = [
  { key: "paid", label: "Paid", statuses: ["paid"], color: "bg-green-600", textColor: "text-green-700", bgColor: "bg-green-50", borderColor: "border-green-200" },
  { key: "balance_due", label: "Balance Due", statuses: ["partial_paid", "deposit_paid"], color: "bg-orange-500", textColor: "text-orange-700", bgColor: "bg-orange-50", borderColor: "border-orange-200" },
  { key: "accepted", label: "Accepted", statuses: ["accepted", "signed"], color: "bg-amber-500", textColor: "text-amber-700", bgColor: "bg-amber-50", borderColor: "border-amber-200" },
  { key: "open", label: "Open", statuses: ["draft", "sent"], color: "bg-blue-500", textColor: "text-blue-700", bgColor: "bg-blue-50", borderColor: "border-blue-200" },
  { key: "declined", label: "Declined", statuses: ["voided", "inactive"], color: "bg-red-500", textColor: "text-red-700", bgColor: "bg-red-50", borderColor: "border-red-200" },
];

const FOLLOWUP_STATUS_CLASSES = {
  ready_to_proceed: "bg-green-100 text-green-700",
  considering: "bg-amber-100 text-amber-700",
  not_now: "bg-red-100 text-red-600",
};

const FOLLOWUP_STATUS_LABELS = {
  ready_to_proceed: "Ready to go",
  considering: "Still thinking",
  not_now: "Not now",
};

const getFollowupStatusClass = (status) =>
  FOLLOWUP_STATUS_CLASSES[status] || "bg-zinc-100 text-zinc-600";

const getFollowupStatusLabel = (status) =>
  FOLLOWUP_STATUS_LABELS[status] || status;

const renderTrackingCell = (quote, tracking) => {
  const isCardPayment = !["cash", "check"].includes(quote.payment_method);
  if (!isCardPayment) return null;
  if (quote.status === "draft") return <span className="text-xs text-zinc-400">-</span>;
  if (!tracking) return <span className="text-xs text-zinc-400">Loading...</span>;
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1" title={`Email opened ${tracking.total_opens} time(s)`}>
        {tracking.total_opens > 0
          ? <MailOpen className="w-4 h-4 text-green-600" />
          : <Mail className="w-4 h-4 text-zinc-300" />}
        <span className={`text-xs ${tracking.total_opens > 0 ? "text-green-600 font-medium" : "text-zinc-400"}`}>
          {tracking.total_opens}
        </span>
      </div>
      <div className="flex items-center gap-1" title={`Link clicked ${tracking.total_clicks} time(s)`}>
        <MousePointerClick className={`w-4 h-4 ${tracking.total_clicks > 0 ? "text-blue-600" : "text-zinc-300"}`} />
        <span className={`text-xs ${tracking.total_clicks > 0 ? "text-blue-600 font-medium" : "text-zinc-400"}`}>
          {tracking.total_clicks}
        </span>
      </div>
    </div>
  );
};

const getStatusBadge = (status) => {
  const variants = {
    draft: { cls: "bg-zinc-100 text-zinc-600", label: "Draft" },
    sent: { cls: "bg-blue-100 text-blue-700", label: "Sent" },
    signed: { cls: "bg-amber-100 text-amber-700", label: "Signed" },
    accepted: { cls: "bg-amber-100 text-amber-700", label: "Accepted" },
    deposit_paid: { cls: "bg-green-100 text-green-800", label: "Deposit Paid" },
    partial_paid: { cls: "bg-green-100 text-green-800", label: "Partial Paid" },
    paid: { cls: "bg-[#00341d] text-white", label: "Paid" },
    voided: { cls: "bg-red-100 text-red-700", label: "Declined" },
    inactive: { cls: "bg-zinc-200 text-zinc-600", label: "Inactive" },
  };
  const v = variants[status] || variants.draft;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${v.cls}`}>
      {v.label}
    </span>
  );
};

const getOutstandingBalance = (quote) => {
  const subtotal = Number(quote.subtotal) || 0;
  const discount = Number(quote.discount_amount) || 0;
  const totalBase = Math.max(subtotal - discount, 0);
  if (!["partial_paid", "deposit_paid"].includes(quote.status)) {
    return totalBase;
  }
  // deposit_amount is base $ for cash/check; with-fee for card (Stripe webhook).
  const rawDeposit = Number(quote.deposit_amount) || 0;
  const paidBase = quote.payment_method === "card" ? rawDeposit / 1.03 : rawDeposit;
  return Math.max(totalBase - paidBase, 0);
};

const QuoteRow = ({ quote, tracking, actions, changeOrderCount }) => {
  const isBalanceDue = ["partial_paid", "deposit_paid"].includes(quote.status);
  const outstanding = getOutstandingBalance(quote);
  return (
  <TableRow className="border-b border-zinc-100 hover:bg-zinc-50/50 transition-colors" data-testid={`quote-row-${quote.id}`}>
    <TableCell className="py-3 px-6">
      <div className="flex items-center gap-2">
        <Link
          to={`/quotes/${quote.id}/activity`}
          className="font-medium text-[#00341d] hover:underline hover:text-[#004d2a] transition-colors"
          data-testid={`activity-link-${quote.id}`}
        >
          {quote.quote_number}
        </Link>
        {changeOrderCount > 0 && (
          <button
            onClick={() => actions.viewChangeOrders(quote)}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-xs font-medium hover:bg-violet-200 transition-colors cursor-pointer"
            title={`${changeOrderCount} change order${changeOrderCount > 1 ? "s" : ""}`}
            data-testid={`co-badge-${quote.id}`}
          >
            <FileText className="w-3 h-3" />
            {changeOrderCount} CO
          </button>
        )}
      </div>
    </TableCell>
    <TableCell className="py-3 px-6">
      <div>
        <p className="font-medium text-zinc-900">{quote.customer_name}</p>
        <p className="text-sm text-zinc-500">{quote.customer_email}</p>
      </div>
    </TableCell>
    <TableCell className="py-3 px-6 font-semibold" data-testid={`quote-row-amount-${quote.id}`}>
      <div className="flex flex-col">
        <span>${outstanding.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
        {isBalanceDue && outstanding < (Number(quote.subtotal) || 0) && (
          <span className="text-[10px] font-normal text-zinc-400 leading-tight">
            of ${Number(quote.subtotal).toLocaleString(undefined, { maximumFractionDigits: 0 })} due
          </span>
        )}
      </div>
    </TableCell>
    <TableCell className="py-3 px-6">{getStatusBadge(quote.status)}</TableCell>
    <TableCell className="py-3 px-6">
      {quote.payment_method === "check" && quote.check_number && (
        <span className="text-xs text-zinc-500">Check #{quote.check_number}</span>
      )}
      {quote.payment_method === "cash" && (
        <span className="text-xs text-zinc-500">Cash</span>
      )}
      {renderTrackingCell(quote, tracking)}
    </TableCell>
    <TableCell className="py-3 px-6 text-zinc-500">{new Date(quote.created_at).toLocaleDateString()}</TableCell>
    <TableCell className="py-3 px-6">
      {quote.followup_count > 0 ? (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <MailPlus className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs text-blue-600 font-medium">
              Sent {quote.followup_count > 1 ? `(${quote.followup_count}x)` : ""}
              {quote.last_followup_sent && (
                <span className="text-zinc-400 font-normal ml-1">
                  {new Date(quote.last_followup_sent).toLocaleDateString()}
                </span>
              )}
            </span>
          </div>
          {quote.followup_status && (
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getFollowupStatusClass(quote.followup_status)}`}>
              {getFollowupStatusLabel(quote.followup_status)}
            </span>
          )}
        </div>
      ) : (
        <span className="text-xs text-zinc-300">-</span>
      )}
    </TableCell>
    <TableCell className="py-3 px-6 text-right">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`quote-actions-${quote.id}`}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => window.open(`/quote/${quote.id}`, "_blank")}>
            <Eye className="w-4 h-4 mr-2" /> View Quote
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => actions.viewActivity(quote)}>
            <Clock className="w-4 h-4 mr-2" /> Activity Log
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => actions.copyLink(quote)}>
            <Link2 className="w-4 h-4 mr-2" /> Copy Link
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => actions.duplicate(quote)}>
            <Copy className="w-4 h-4 mr-2" /> Duplicate
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => actions.changeStatus(quote)} data-testid={`change-status-${quote.id}`}>
            <RefreshCw className="w-4 h-4 mr-2" /> Change Status
          </DropdownMenuItem>
          {!["voided", "inactive"].includes(quote.status) && (
            <DropdownMenuItem onClick={() => actions.edit(quote)}>
              <Edit className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
          )}
          {(quote.status === "draft" || quote.status === "sent") && quote.customer_email && (
            <DropdownMenuItem onClick={() => actions.send(quote)}>
              <Send className="w-4 h-4 mr-2" /> Send to Customer
            </DropdownMenuItem>
          )}
          {quote.status === "draft" && (
            <DropdownMenuItem onClick={() => actions.markSent(quote)}>
              <Send className="w-4 h-4 mr-2" /> Mark as Sent
            </DropdownMenuItem>
          )}
          {quote.status === "sent" && quote.customer_email && (
            <DropdownMenuItem onClick={() => actions.followup(quote)} data-testid={`send-followup-${quote.id}`}>
              <MailPlus className="w-4 h-4 mr-2" /> Send Follow-up 1
            </DropdownMenuItem>
          )}
          {/* Send Balance Due - for partially paid quotes */}
          {(quote.status === "partial_paid" || quote.payment_status === "deposit_paid" || quote.deposit_paid) && quote.status !== "paid" && quote.customer_email && (
            <DropdownMenuItem onClick={() => actions.sendBalanceDue(quote)} data-testid={`send-balance-due-${quote.id}`}>
              <DollarSign className="w-4 h-4 mr-2" /> Send Balance Due
            </DropdownMenuItem>
          )}
          {quote.status !== "paid" && quote.status !== "draft" && (
            <DropdownMenuItem onClick={() => actions.markPaid(quote)} data-testid={`mark-paid-${quote.id}`}>
              <Banknote className="w-4 h-4 mr-2" /> Mark as Paid
            </DropdownMenuItem>
          )}
          {quote.status !== "paid" && quote.status !== "draft" && (
            <DropdownMenuItem onClick={() => actions.syncStripe(quote)} data-testid={`sync-stripe-${quote.id}`}>
              <CreditCard className="w-4 h-4 mr-2" /> Sync Payment from Stripe
            </DropdownMenuItem>
          )}
          {(quote.status === "accepted" || quote.deposit_paid || quote.payment_status === "partial_paid") && quote.status !== "paid" && (
            <DropdownMenuItem onClick={() => actions.markComplete(quote)} data-testid={`mark-complete-${quote.id}`}>
              <CheckCircle className="w-4 h-4 mr-2" /> Mark Complete
            </DropdownMenuItem>
          )}
          {(quote.status === "accepted" || quote.status === "paid" || quote.deposit_paid) && (
            <DropdownMenuItem onClick={() => actions.changeOrder(quote)} data-testid={`change-order-${quote.id}`}>
              <FileText className="w-4 h-4 mr-2" /> Add Change Order
            </DropdownMenuItem>
          )}
          {changeOrderCount > 0 && (
            <DropdownMenuItem onClick={() => actions.viewChangeOrders(quote)} data-testid={`view-change-orders-${quote.id}`}>
              <Eye className="w-4 h-4 mr-2" /> View Change Orders ({changeOrderCount})
            </DropdownMenuItem>
          )}
          {(quote.status === "sent" || quote.status === "accepted") && (
            <DropdownMenuItem onClick={() => actions.decline(quote.id)} className="text-red-600 cursor-pointer" data-testid={`decline-quote-${quote.id}`}>
              <XCircle className="w-4 h-4 mr-2" /> Mark as Declined
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => actions.delete(quote.id)} className="text-red-600 cursor-pointer" data-testid={`delete-quote-${quote.id}`}>
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  </TableRow>
  );
};

const QuoteTable = ({ quotes, trackingData, actions, changeOrderCounts }) => (
  <Table className="quote-table">
    <TableHeader>
      <TableRow className="bg-zinc-50 border-b border-zinc-100">
        <TableHead className="py-3 px-6">Quote #</TableHead>
        <TableHead className="py-3 px-6">Customer</TableHead>
        <TableHead className="py-3 px-6">Total</TableHead>
        <TableHead className="py-3 px-6">Status</TableHead>
        <TableHead className="py-3 px-6">Activity</TableHead>
        <TableHead className="py-3 px-6">Created</TableHead>
        <TableHead className="py-3 px-6">Follow-Up</TableHead>
        <TableHead className="py-3 px-6 text-right">Actions</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {quotes.map((q) => (
        <QuoteRow key={q.id} quote={q} tracking={trackingData[q.id]} actions={actions} changeOrderCount={changeOrderCounts[q.id] || 0} />
      ))}
    </TableBody>
  </Table>
);

const DashboardPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [trackingData, setTrackingData] = useState({});
  const [followupDialog, setFollowupDialog] = useState({ open: false, quote: null, email: null, sending: false });
  const [markPaidDialog, setMarkPaidDialog] = useState({ open: false, quote: null, method: "check", checkNumber: "", paymentType: "full", amount: "", submitting: false });
  const [completeDialog, setCompleteDialog] = useState({ open: false, quote: null, sending: false });
  const [balanceDueDialog, setBalanceDueDialog] = useState({ open: false, quote: null, sending: false });
  const [changeOrderDialog, setChangeOrderDialog] = useState({
    open: false,
    quote: null,
    lineItems: [],
    adjustmentAmount: 0,
    adjustmentDescription: "",
    notes: "",
    requiresApproval: false,
    saving: false
  });
  const [activeFilter, setActiveFilter] = useState("all");
  const [changeOrderCounts, setChangeOrderCounts] = useState({});
  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [viewCODialog, setViewCODialog] = useState({ open: false, quote: null, changeOrders: [], loading: false });
  const [statusChangeDialog, setStatusChangeDialog] = useState({ open: false, quote: null, newStatus: "", submitting: false });
  const [sendQuoteDialog, setSendQuoteDialog] = useState({ open: false, quote: null });
  const [uploadQuoteOpen, setUploadQuoteOpen] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => { fetchQuotes(); fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`);
      setAllCustomers(res.data || []);
    } catch (err) { console.error("Failed to load customers (non-critical):", err?.message); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("zoho") === "connected" || params.get("zoho") === "error") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const fetchTracking = async () => {
      const sentQuotes = quotes.filter((q) => q.status !== "draft");
      for (const quote of sentQuotes) {
        try {
          const response = await axios.get(`${API}/quotes/${quote.id}/tracking`);
          setTrackingData((prev) => ({ ...prev, [quote.id]: response.data }));
        } catch (error) { console.debug("Tracking fetch skipped", error?.message); }
      }
    };
    if (quotes.length > 0) fetchTracking();
  }, [quotes]);

  useEffect(() => {
    const fetchCOCounts = async () => {
      const counts = {};
      for (const quote of quotes) {
        try {
          const response = await axios.get(`${API}/quotes/${quote.id}/change-orders`);
          if (response.data.length > 0) counts[quote.id] = response.data.length;
        } catch (err) { console.error(`Failed to fetch CO count for quote ${quote.id}:`, err?.message); }
      }
      setChangeOrderCounts(counts);
    };
    if (quotes.length > 0) fetchCOCounts();
  }, [quotes]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm("Delete this quote permanently? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      toast.success("Quote deleted");
      fetchQuotes();
    } catch (error) { toast.error("Failed to delete quote"); }
  };

  const handleDecline = async (quoteId) => {
    if (!window.confirm("Mark this quote as declined? This will move it to the Older Quotes section.")) return;
    try {
      await axios.patch(`${API}/quotes/${quoteId}/void`);
      toast.success("Quote marked as declined");
      fetchQuotes();
    } catch (error) { toast.error("Failed to mark quote as declined"); }
  };

  const handleDuplicate = async (quote) => {
    try {
      const response = await axios.post(`${API}/quotes/${quote.id}/duplicate`);
      toast.success("Quote duplicated");
      navigate(`/quotes/${response.data.id}/edit`);
    } catch (error) { toast.error("Failed to duplicate quote"); }
  };

  const handleSendQuote = (quote) => {
    if (!quote.customer_email) {
      toast.error("Customer email is missing");
      return;
    }
    setSendQuoteDialog({ open: true, quote });
  };

  const handleCopyLink = async (quote) => {
    let url = quote.short_url;
    if (!url) {
      const toastId = "shorten-quote";
      toast.loading("Generating short link...", { id: toastId });
      try {
        const resp = await axios.post(`${API}/quotes/${quote.id}/shorten`);
        url = resp.data.short_url;
        toast.dismiss(toastId);
      } catch {
        toast.dismiss(toastId);
        const baseUrl = window.location.hostname.includes("landclearco.com")
          ? "https://quotes.landclearco.com"
          : window.location.origin;
        url = `${baseUrl}/quote/${quote.id}`;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Short link copied!");
  };

  const handleMarkAsSent = async (quote) => {
    try {
      await axios.post(`${API}/quotes/${quote.id}/mark-sent`);
      toast.success("Quote marked as sent");
      fetchQuotes();
    } catch (error) { toast.error("Failed to mark quote as sent"); }
  };

  // Mark Complete
  const handleMarkComplete = (quote) => {
    const isFullyPaid = quote.balance_paid || quote.payment_status === "paid";
    const hasDepositOnly = quote.deposit_paid || quote.payment_status === "deposit_paid";
    const isPartialPaid = quote.payment_status === "partial_paid";
    const isAcceptedUnpaid = (quote.status === "accepted" || quote.status === "signed") &&
                              !isFullyPaid && !hasDepositOnly && !isPartialPaid;
    const needsBalancePayment = !isFullyPaid && (hasDepositOnly || isPartialPaid || isAcceptedUnpaid);
    setCompleteDialog({ open: true, quote, needsBalancePayment, sending: false });
  };

  const handleSendBalanceEmail = async () => {
    if (!completeDialog.quote) return;
    setCompleteDialog((prev) => ({ ...prev, sending: true }));
    try {
      await axios.post(`${API}/quotes/${completeDialog.quote.id}/send-balance-due`);
      toast.success("Balance due email sent to customer!");
      await axios.post(`${API}/quotes/${completeDialog.quote.id}/mark-complete`);
      setCompleteDialog({ open: false, quote: null, sending: false });
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
      setCompleteDialog((prev) => ({ ...prev, sending: false }));
    }
  };

  const handleMarkCompleteOnly = async () => {
    if (!completeDialog.quote) return;
    setCompleteDialog((prev) => ({ ...prev, sending: true }));
    try {
      await axios.post(`${API}/quotes/${completeDialog.quote.id}/mark-complete`);
      toast.success("Service marked as complete!");
      setCompleteDialog({ open: false, quote: null, sending: false });
      fetchQuotes();
    } catch (error) {
      toast.error("Failed to mark complete");
      setCompleteDialog((prev) => ({ ...prev, sending: false }));
    }
  };

  // Send Balance Due (standalone - separate from Mark Complete flow)
  const handleSendBalanceDue = async (payload = {}) => {
    if (!balanceDueDialog.quote) return;
    setBalanceDueDialog((prev) => ({ ...prev, sending: true }));
    try {
      await axios.post(
        `${API}/quotes/${balanceDueDialog.quote.id}/send-balance-due`,
        { ...payload, origin_url: window.location.origin },
      );
      toast.success("Balance due email sent to customer!");
      setBalanceDueDialog({ open: false, quote: null, sending: false });
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send balance due email");
      setBalanceDueDialog((prev) => ({ ...prev, sending: false }));
    }
  };

  // Sync a missed Stripe payment from Stripe's own record (webhook recovery)
  const handleSyncStripe = async (quote) => {
    const t = toast.loading(`Checking Stripe for ${quote.quote_number}...`);
    try {
      const res = await axios.post(`${API}/quotes/${quote.id}/sync-stripe-payment`);
      if (res.data?.synced > 0) {
        toast.success(res.data.message || "Payment synced from Stripe", { id: t });
      } else {
        toast.info(res.data?.message || "No paid payments found in Stripe for this quote.", { id: t });
      }
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not sync from Stripe", { id: t });
    }
  };

  // Change Order handlers - navigate to builder page
  const handleOpenChangeOrder = (quote) => {
    navigate(`/quotes/${quote.id}/change-orders/new`);
  };

  const handleAddChangeOrderLineItem = () => {
    setChangeOrderDialog(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { id: Date.now().toString(), service_name: "", description: "", quantity: 1, unit: "", unit_price: 0, total: 0 }]
    }));
  };

  const handleUpdateChangeOrderLineItem = (id, field, value) => {
    setChangeOrderDialog(prev => {
      const newItems = prev.lineItems.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "quantity" || field === "unit_price") {
            updated.total = (updated.quantity || 0) * (updated.unit_price || 0);
          }
          return updated;
        }
        return item;
      });
      return { ...prev, lineItems: newItems };
    });
  };

  const handleRemoveChangeOrderLineItem = (id) => {
    setChangeOrderDialog(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter(item => item.id !== id)
    }));
  };

  const handleSaveChangeOrder = async (sendAfterSave = false) => {
    if (!changeOrderDialog.quote) return;
    setChangeOrderDialog(prev => ({ ...prev, saving: true }));
    try {
      const data = {
        quote_id: changeOrderDialog.quote.id,
        line_items: changeOrderDialog.lineItems.filter(item => item.service_name),
        adjustment_amount: parseFloat(changeOrderDialog.adjustmentAmount) || 0,
        adjustment_description: changeOrderDialog.adjustmentDescription,
        notes: changeOrderDialog.notes,
        requires_approval: changeOrderDialog.requiresApproval
      };
      const response = await axios.post(`${API}/quotes/${changeOrderDialog.quote.id}/change-orders`, data);
      if (sendAfterSave) {
        await axios.post(`${API}/change-orders/${response.data.id}/send`, {}, {
          headers: { origin: window.location.origin }
        });
        toast.success("Change order created and sent to customer!");
      } else {
        toast.success("Change order created!");
      }
      setChangeOrderDialog({ open: false, quote: null, lineItems: [], adjustmentAmount: 0, adjustmentDescription: "", notes: "", requiresApproval: false, saving: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create change order");
      setChangeOrderDialog(prev => ({ ...prev, saving: false }));
    }
  };

  // Follow-up
  const handleGenerateFollowup = async (quote) => {
    try {
      const response = await axios.get(`${API}/quotes/${quote.id}/followup/followup_1`);
      setFollowupDialog({ open: true, quote, email: response.data, sending: false });
    } catch (error) { toast.error("Failed to generate follow-up email"); }
  };

  const handleSendFollowup = async () => {
    if (!followupDialog.quote || !followupDialog.email) return;
    setFollowupDialog((prev) => ({ ...prev, sending: true }));
    try {
      await axios.post(`${API}/quotes/${followupDialog.quote.id}/followup/${followupDialog.email.template_id}/send`);
      toast.success("Follow-up email sent!");
      setFollowupDialog({ open: false, quote: null, email: null, sending: false });
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send follow-up email");
      setFollowupDialog((prev) => ({ ...prev, sending: false }));
    }
  };

  // Mark Paid
  const handleMarkPaidSubmit = async () => {
    const { quote, method, checkNumber, paymentType, amount } = markPaidDialog;
    if (!quote) return;
    if (method === "check" && !checkNumber.trim()) {
      toast.error("Please enter a check number");
      return;
    }
    if (paymentType === "partial" && (!amount || parseFloat(amount) <= 0)) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    setMarkPaidDialog((prev) => ({ ...prev, submitting: true }));
    try {
      const payload = {
        payment_method: method,
        payment_type: paymentType,
        check_number: method === "check" ? checkNumber.trim() : undefined,
      };
      // Always send the amount when provided (full-in-full-for-less / overage / partial).
      if (amount !== "" && amount != null) {
        payload.amount = parseFloat(amount);
      }
      await axios.post(`${API}/quotes/${quote.id}/mark-paid`, payload);
      const label = paymentType === "partial" ? `$${parseFloat(amount).toLocaleString()} partial payment recorded` : `Quote marked as paid by ${method}`;
      toast.success(label);
      setMarkPaidDialog({ open: false, quote: null, method: "check", checkNumber: "", paymentType: "full", amount: "", submitting: false });
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
      setMarkPaidDialog((prev) => ({ ...prev, submitting: false }));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Zoho removed
  // View Change Orders
  const handleViewChangeOrders = async (quote) => {
    setViewCODialog({ open: true, quote, changeOrders: [], loading: true });
    try {
      const response = await axios.get(`${API}/quotes/${quote.id}/change-orders`);
      setViewCODialog((prev) => ({ ...prev, changeOrders: response.data, loading: false }));
    } catch {
      toast.error("Failed to load change orders");
      setViewCODialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteChangeOrder = async (coId) => {
    try {
      await axios.delete(`${API}/change-orders/${coId}`);
      toast.success("Change order deleted");
      // Refresh the dialog list
      setViewCODialog((prev) => ({
        ...prev,
        changeOrders: prev.changeOrders.filter((co) => co.id !== coId),
      }));
      // Update the count
      if (viewCODialog.quote) {
        setChangeOrderCounts((prev) => {
          const newCounts = { ...prev };
          const qid = viewCODialog.quote.id;
          newCounts[qid] = Math.max(0, (newCounts[qid] || 1) - 1);
          if (newCounts[qid] === 0) delete newCounts[qid];
          return newCounts;
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete change order");
    }
  };

  // Status Change
  const handleStatusChangeSubmit = async () => {
    const { quote, newStatus } = statusChangeDialog;
    if (!quote || !newStatus) return;
    setStatusChangeDialog((prev) => ({ ...prev, submitting: true }));
    try {
      await axios.put(`${API}/quotes/${quote.id}/status`, { status: newStatus });
      toast.success(`Status updated to "${newStatus}"`);
      setStatusChangeDialog({ open: false, quote: null, newStatus: "", submitting: false });
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
      setStatusChangeDialog((prev) => ({ ...prev, submitting: false }));
    }
  };

  // Filters & Stats
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  const filteredQuotes = quotes.filter(
    (q) =>
      q.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.quote_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const OLDER_STATUSES = ["sent", "voided"];
  const recentQuotes = filteredQuotes.filter(
    (q) => new Date(q.created_at) >= oneMonthAgo || !OLDER_STATUSES.includes(q.status)
  );
  const olderQuotes = filteredQuotes.filter(
    (q) => new Date(q.created_at) < oneMonthAgo && OLDER_STATUSES.includes(q.status)
  );

  const groupedQuotes = STATUS_GROUPS.map((group) => {
    const groupQuotes = recentQuotes.filter((q) => group.statuses.includes(q.status));
    return {
      ...group,
      quotes: groupQuotes,
      // For Balance Due group, sum the OUTSTANDING balance (subtotal - paid),
      // not the gross quote total — admin wants to see "how much is still owed".
      totalValue: groupQuotes.reduce(
        (s, q) => s + (group.key === "balance_due" ? getOutstandingBalance(q) : (q.subtotal || 0)),
        0
      ),
    };
  });

  const olderGroupedQuotes = STATUS_GROUPS.map((group) => ({
    ...group,
    quotes: olderQuotes.filter((q) => group.statuses.includes(q.status)),
  })).filter((g) => g.quotes.length > 0);

  const olderTotalValue = olderQuotes.reduce((s, q) => s + (q.subtotal || 0), 0);

  const stats = {
    total: quotes.length,
    draft: quotes.filter((q) => q.status === "draft").length,
    sent: quotes.filter((q) => q.status === "sent").length,
    paid: quotes.filter((q) => q.status === "paid").length,
    balanceDue: quotes.filter((q) => ["partial_paid", "deposit_paid"].includes(q.status)).length,
    totalValue: quotes.reduce((sum, q) => sum + (q.subtotal || 0), 0),
    acceptedValue: quotes.filter((q) => ["signed", "accepted", "deposit_paid", "partial_paid", "paid"].includes(q.status)).reduce((sum, q) => sum + (q.subtotal || 0), 0),
    paidValue: quotes.filter((q) => q.status === "paid").reduce((sum, q) => sum + (q.subtotal || 0), 0),
    balanceDueValue: quotes.filter((q) => ["partial_paid", "deposit_paid"].includes(q.status)).reduce((sum, q) => sum + getOutstandingBalance(q), 0),
  };
  stats.arValue = stats.acceptedValue - stats.paidValue;

  const actions = {
    edit: (q) => navigate(`/quotes/${q.id}/edit`),
    viewActivity: (q) => navigate(`/quotes/${q.id}/activity`),
    send: handleSendQuote,
    copyLink: handleCopyLink,
    duplicate: handleDuplicate,
    followup: handleGenerateFollowup,
    delete: handleDelete,
    decline: handleDecline,
    markPaid: (q) => setMarkPaidDialog({ open: true, quote: q, method: "check", checkNumber: "", paymentType: "full", amount: "", submitting: false }),
    markSent: handleMarkAsSent,
    markComplete: handleMarkComplete,
    changeOrder: handleOpenChangeOrder,
    sendBalanceDue: (q) => setBalanceDueDialog({ open: true, quote: q, sending: false }),
    syncStripe: handleSyncStripe,
    viewChangeOrders: handleViewChangeOrders,
    changeStatus: (q) => setStatusChangeDialog({ open: true, quote: q, newStatus: q.status, submitting: false }),
  };

  return (
    <div className="min-h-screen bg-zinc-50/50 flex" data-testid="dashboard-page">
      <Sidebar onLogout={() => { logout(); navigate("/login"); }} />

      <main className="flex-1 overflow-auto">
        <header className="bg-white border-b border-zinc-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-[#00341d]">Quote Dashboard</h1>
              <p className="text-zinc-500 mt-1">Manage your quotes and proposals</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setPaymentLinkOpen(true)}
                variant="outline"
                className="border-zinc-300 text-zinc-700 font-medium px-4 h-11 rounded-sm hover:bg-zinc-50 hidden sm:flex"
                data-testid="create-payment-link-btn"
              >
                <Link2 className="w-4 h-4 mr-2" /> Payment Link
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/proposals")}
                className="border-[#00341d]/30 text-[#00341d] hover:bg-[#00341d]/5 font-bold uppercase tracking-wide px-4 h-11 rounded-sm"
                data-testid="proposals-nav-btn"
              >
                <FileText className="w-4 h-4 mr-2" /> Proposals
              </Button>
              <Button
                variant="outline"
                onClick={() => setUploadQuoteOpen(true)}
                className="border-[#00341d]/30 text-[#00341d] hover:bg-[#00341d]/5 font-bold uppercase tracking-wide px-4 h-11 rounded-sm"
                data-testid="upload-quote-button"
              >
                <Upload className="w-4 h-4 mr-2" /> Upload Quote
              </Button>
              <Button onClick={() => navigate("/quotes/new")} className="bg-[#d97706] hover:bg-[#b45309] text-white font-bold uppercase tracking-wide px-6 h-11 rounded-sm shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5" data-testid="new-quote-button">
                <Plus className="w-5 h-5 mr-2" /> New Quote
              </Button>
            </div>
          </div>
        </header>

        <div className="p-8">
          <StatsCards stats={stats} />

          {/* Search + Quick Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input placeholder="Search quotes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-10 bg-white border-zinc-200" data-testid="search-input" />
            </div>
            <div className="flex gap-2 flex-wrap" data-testid="quick-filters">
              <button
                onClick={() => setActiveFilter("all")}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${activeFilter === "all" ? "bg-[#00341d] text-white shadow-sm" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                data-testid="filter-all"
              >
                All ({recentQuotes.length})
              </button>
              {groupedQuotes.map((g) => (
                <button
                  key={g.key}
                  onClick={() => setActiveFilter(activeFilter === g.key ? "all" : g.key)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${activeFilter === g.key ? `${g.color} text-white shadow-sm` : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                  data-testid={`filter-${g.key}`}
                >
                  <span className={`w-2 h-2 rounded-full ${activeFilter === g.key ? "bg-white/70" : g.color}`} />
                  {g.label} ({g.quotes.length})
                </button>
              ))}
            </div>
          </div>

          {/* Grouped Accordion */}
          {(() => {
            const visibleGroups = activeFilter === "all" ? groupedQuotes : groupedQuotes.filter((g) => g.key === activeFilter);
            const openKeys = visibleGroups.filter((g) => g.quotes.length > 0).map((g) => g.key);

            if (loading) return (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00341d]" />
              </div>
            );
            if (filteredQuotes.length === 0) return (
              <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm">
                <CardContent className="text-center py-16">
                  <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                  <p className="text-zinc-500">No quotes found</p>
                  <Button onClick={() => navigate("/quotes/new")} className="mt-4 bg-[#00341d] hover:bg-[#002816]">Create Your First Quote</Button>
                </CardContent>
              </Card>
            );
            return (
              <>
                <Accordion type="multiple" defaultValue={openKeys} key={activeFilter} className="space-y-3" data-testid="quotes-accordion">
                  {visibleGroups.map((group) => (
                    <AccordionItem key={group.key} value={group.key} className={`border rounded-lg overflow-hidden ${group.borderColor} bg-white shadow-sm`} data-testid={`group-${group.key}`}>
                      <AccordionTrigger className={`px-6 py-4 hover:no-underline ${group.bgColor}`}>
                        <div className="flex items-center gap-3">
                          <span className={`w-3 h-3 rounded-full ${group.color}`} />
                          <span className={`font-heading text-lg font-bold uppercase tracking-tight ${group.textColor}`}>
                            {group.label}
                          </span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${group.color} text-white`}>
                            {group.quotes.length}
                          </span>
                          <span className="text-sm font-normal text-zinc-500 ml-2">
                            ${group.totalValue.toLocaleString()}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-0 pb-0">
                        {group.quotes.length === 0 ? (
                          <p className="text-sm text-zinc-400 px-6 py-4">No quotes in this group</p>
                        ) : (
                          <QuoteTable quotes={group.quotes} trackingData={trackingData} actions={actions} changeOrderCounts={changeOrderCounts} />
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* Older Quotes Section */}
                {olderQuotes.length > 0 && activeFilter === "all" && (
                  <div className="mt-10" data-testid="older-quotes-section">
                    <Accordion type="single" collapsible className="space-y-0">
                      <AccordionItem value="older" className="border border-zinc-300 rounded-lg overflow-hidden bg-white shadow-sm">
                        <AccordionTrigger className="px-6 py-4 hover:no-underline bg-zinc-100">
                          <div className="flex items-center gap-3">
                            <Clock className="w-4 h-4 text-zinc-400" />
                            <span className="font-heading text-lg font-bold uppercase tracking-tight text-zinc-500">
                              Older Quotes
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-zinc-400 text-white">
                              {olderQuotes.length}
                            </span>
                            <span className="text-sm font-normal text-zinc-400 ml-2">
                              ${olderTotalValue.toLocaleString()} &middot; Created more than 30 days ago
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-0 pb-0">
                          {olderGroupedQuotes.map((group) => (
                            <div key={group.key}>
                              <div className={`px-6 py-2 ${group.bgColor} border-t border-zinc-100 flex items-center gap-2`}>
                                <span className={`w-2 h-2 rounded-full ${group.color}`} />
                                <span className={`text-sm font-bold uppercase tracking-wider ${group.textColor}`}>{group.label}</span>
                                <span className="text-xs text-zinc-400">({group.quotes.length})</span>
                              </div>
                              <QuoteTable quotes={group.quotes} trackingData={trackingData} actions={actions} changeOrderCounts={changeOrderCounts} />
                            </div>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </main>

      {/* Dialogs */}
      <MarkPaidDialog dialog={markPaidDialog} setDialog={setMarkPaidDialog} onSubmit={handleMarkPaidSubmit} />
      <FollowupEmailDialog dialog={followupDialog} setDialog={setFollowupDialog} onSend={handleSendFollowup} copyToClipboard={copyToClipboard} />
      <MarkCompleteDialog dialog={completeDialog} setDialog={setCompleteDialog} onSendBalanceEmail={handleSendBalanceEmail} onMarkCompleteOnly={handleMarkCompleteOnly} />
      <ChangeOrderDialog
        dialog={changeOrderDialog}
        setDialog={setChangeOrderDialog}
        onAddLineItem={handleAddChangeOrderLineItem}
        onUpdateLineItem={handleUpdateChangeOrderLineItem}
        onRemoveLineItem={handleRemoveChangeOrderLineItem}
        onSave={handleSaveChangeOrder}
      />
      <SendBalanceDueDialog dialog={balanceDueDialog} setDialog={setBalanceDueDialog} onSend={handleSendBalanceDue} />
      <SendQuoteEmailDialog
        open={sendQuoteDialog.open}
        onOpenChange={(open) => setSendQuoteDialog((p) => ({ ...p, open }))}
        quote={sendQuoteDialog.quote}
        onSent={() => {
          fetchQuotes();
        }}
      />
      <ViewChangeOrdersDialog dialog={viewCODialog} setDialog={setViewCODialog} onDelete={handleDeleteChangeOrder} />
      <CreatePaymentLinkDialog
        open={paymentLinkOpen}
        onClose={() => setPaymentLinkOpen(false)}
        customers={allCustomers}
      />

      <UploadQuoteDialog
        open={uploadQuoteOpen}
        onOpenChange={setUploadQuoteOpen}
        customers={allCustomers}
        onCreated={fetchQuotes}
      />

      {/* Status Change Dialog */}
      <Dialog open={statusChangeDialog.open} onOpenChange={(open) => !open && setStatusChangeDialog({ open: false, quote: null, newStatus: "", submitting: false })}>
        <DialogContent className="max-w-sm" data-testid="status-change-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-[#00341d]">
              Change Status
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-zinc-500 mb-4">
              Quote <strong>{statusChangeDialog.quote?.quote_number}</strong> &mdash; {statusChangeDialog.quote?.customer_name}
            </p>
            <Select
              value={statusChangeDialog.newStatus}
              onValueChange={(val) => setStatusChangeDialog((prev) => ({ ...prev, newStatus: val }))}
              data-testid="status-select"
            >
              <SelectTrigger className="w-full" data-testid="status-select-trigger">
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                <SelectItem value="partial_paid">Partial Paid</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="voided">Declined</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStatusChangeDialog({ open: false, quote: null, newStatus: "", submitting: false })}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleStatusChangeSubmit}
              disabled={statusChangeDialog.submitting || !statusChangeDialog.newStatus}
              className="bg-[#00341d] hover:bg-[#002816] text-white rounded-sm"
              data-testid="status-change-confirm-btn"
            >
              {statusChangeDialog.submitting ? "Saving..." : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
